import { salesApi } from '../config/apiClient';
import { SALES_API } from '../config/api';

// ========== Types ==========

export interface ClubEntryScreenSettingsDto {
  id?: number;
  quickSaleScreenId: number;
  isEnabled: boolean;
  entryMode: string;
  cardMode: string;
  depositEnabled: boolean;
  defaultDepositAmount: number;
  forcePavoPay: boolean;
  directTriggerEnabled: boolean;
  accessControlDoorIds?: string | null;
  /** QR menüden gelen TicketReservations'ı kulüp girişi modunda listele butonu açık mı? */
  reservationSystemEnabled?: boolean;
  /** Ödeme anında kart okutma/atama akışı çalışsın mı? (varsayılan açık) */
  cardScanModalEnabled?: boolean;
  /** Sağdaki canlı kart izleme paneli gösterilsin mi? (varsayılan açık) */
  cardMonitoringPanelEnabled?: boolean;
}

export interface UpdateClubEntrySettingsDto {
  isEnabled: boolean;
  entryMode: string;
  cardMode: string;
  depositEnabled: boolean;
  defaultDepositAmount: number;
  forcePavoPay: boolean;
  directTriggerEnabled: boolean;
  accessControlDoorIds?: string | null;
  reservationSystemEnabled?: boolean;
  cardScanModalEnabled?: boolean;
  cardMonitoringPanelEnabled?: boolean;
}

export interface CardEntitlementItemDto {
  productId: string;
  productName: string;
  quantity: number;
  unitPricePaid: number;
}

export interface CardAssignmentItemDto {
  cardUID: string;
  cardLabel?: string;
  depositAmount: number;
  depositPavoPaySaleItemId?: number;
  depositPavoPayPaymentId?: number;
  serviceProductId?: string;
  serviceProductName?: string;
  servicePavoPaySaleItemId?: number;
  entryMode: string;
  totalEntryRights: number;
  /** Karta tanımlanacak ön ödemeli ürün hakları (paket grubundan). */
  entitlements?: CardEntitlementItemDto[];
}

export interface CardEntitlementDto {
  id: number;
  clubEntrySaleCardId: number;
  cardUID: string;
  productId: string;
  productName: string;
  quantity: number;
  consumedQuantity: number;
  remaining: number;
  unitPricePaid: number;
  status: string;
}

export interface ConsumeEntitlementItem {
  productId: string;
  quantity: number;
  warehouseId?: string;
  vatRate?: number;
}

export interface AssignCardsDto {
  saleReceiptId: number;
  saleReceiptNumber?: string;
  pavoPaySaleId?: number;
  pavoPaySaleNumber?: string;
  depositPavoPaySaleNumber?: string;
  screenId?: number;
  paymentMethod: string;
  // Depozito tahsilat yöntemi: aktif girişlerde depozito etiketi (Nakit/POS)
  // bu alana göre gösterilir.
  depositPaymentMethod?: 'CASH' | 'POS';
  // Depozito ana satıştan AYRI tahsil edildiyse true; backend DepositAsync ile
  // muhasebe kaydını kendisi oluşturur (2 adımlı kulüp girişi akışı).
  collectDepositSeparately?: boolean;
  depositBankAccountId?: number;
  depositCashboxId?: number;
  cards: CardAssignmentItemDto[];
}

export interface AssignCardsFromReservationDto {
  ticketReservationId: number;
  screenId?: number;
  depositPaymentMethod: 'CASH' | 'POS';
  depositCashboxId?: number;
  depositBankAccountId?: number;
  depositPavoPaySaleNumber?: string;
  cards: CardAssignmentItemDto[];
  /**
   * POS akışında deposito QuickSale CompleteSale → POS işlemi sırasında zaten tahsil edilmişse
   * true gönder. Backend bu durumda muhasebeye ikinci kez Deposit kaydı düşmez.
   */
  depositAlreadyCollected?: boolean;
}

export interface ClubEntrySaleCardDto {
  id: number;
  companyId: string;
  screenId?: number;
  saleReceiptId?: number;
  ticketReservationId?: number;
  saleReceiptNumber?: string;
  pavoPaySaleId?: number;
  pavoPaySaleNumber?: string;
  depositPavoPaySaleNumber?: string;
  depositPaymentMethod?: 'CASH' | 'POS' | string;
  cardUID: string;
  cardLabel?: string;
  /** Kart Yönetimi'nde karta atanmış cari adı (CardUID ile eşleştirilir). */
  cardAccountName?: string;
  depositAmount: number;
  depositPavoPaySaleItemId?: number;
  depositPavoPayPaymentId?: number;
  serviceProductId?: string;
  serviceProductName?: string;
  servicePavoPaySaleItemId?: number;
  entryMode: string;
  totalEntryRights: number;
  usedEntryRights: number;
  remainingEntryRights: number;
  status: string;
  isReturned: boolean;
  returnedAt?: string;
  returnedBy?: string;
  depositRefunded: boolean;
  depositRefundedAt?: string;
  paymentMethod: string;
  createdAt: string;
  createdBy?: string;
  lastEntryDate?: string;
  isInside: boolean;
  insideCount: number;
  lastPassDirection?: string;
  lastPassTime?: string;
  entryCount: number;
  exitCount: number;
}

export interface CardLookupResultDto {
  card: ClubEntrySaleCardDto;
  relatedCards: ClubEntrySaleCardDto[];
}

/** Kart Yönetimi'nden satışsız geçiş hakkı tanımlama isteği. */
export interface GrantEntryRightsDto {
  cardUID: string;
  cardLabel?: string;
  entryMode: string; // SINGLE_ENTRY | UNLIMITED_DAILY
  totalEntryRights: number;
  screenId?: number;
  createdBy?: string;
}

/** Kart listesinde "Geçiş Hakkı" sütunu için kart başına kalan hak bilgisi. */
export interface CardEntryRightsDto {
  cardUID: string;
  entryMode: string;
  totalEntryRights: number;
  usedEntryRights: number;
  remainingEntryRights: number;
  status: string;
}

export interface CheckAndConsumeResultDto {
  allowed: boolean;
  denialReason?: string;
  remainingRightsAfter?: number;
  cardUID: string;
  cardLabel?: string;
}

/** Gün sonu raporu için kulüp giriş depozito özeti (satış cirosuna dahil değildir). */
export interface ClubDepositSummaryDto {
  /** Aralıkta tahsil edilen nakit depozito toplamı. */
  collectedCash: number;
  /** Aralıkta tahsil edilen POS depozito toplamı. */
  collectedPos: number;
  /** Tahsil edilen toplam depozito (nakit + POS). */
  collectedTotal: number;
  /** Tahsil edilen depozitolu kart adedi. */
  collectedCount: number;
  /** Aralıkta iade edilen depozito toplamı. */
  refundedTotal: number;
  /** İade edilen kart adedi. */
  refundedCount: number;
  /** Kalan (net) = tahsil edilen - iade edilen. */
  netTotal: number;
}

// ========== Helpers ==========

function toCamelCase(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  const result: any = {};
  for (const key of Object.keys(obj)) {
    const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
    result[camelKey] = toCamelCase(obj[key]);
  }
  return result;
}

// ========== Service ==========

export const clubEntryService = {
  async getSettings(screenId: number): Promise<ClubEntryScreenSettingsDto> {
    const response = await salesApi.get(SALES_API.CLUB_ENTRY_SETTINGS(screenId));
    return toCamelCase(response.data);
  },

  async saveSettings(screenId: number, dto: UpdateClubEntrySettingsDto): Promise<ClubEntryScreenSettingsDto> {
    const response = await salesApi.post(SALES_API.CLUB_ENTRY_SETTINGS(screenId), dto);
    return toCamelCase(response.data);
  },

  async assignCards(dto: AssignCardsDto): Promise<{ success: boolean; message: string; cards: ClubEntrySaleCardDto[] }> {
    const response = await salesApi.post(SALES_API.CLUB_ENTRY_ASSIGN_CARDS, dto);
    return toCamelCase(response.data);
  },

  async assignCardsFromReservation(dto: AssignCardsFromReservationDto): Promise<{ success: boolean; message: string; cards: ClubEntrySaleCardDto[] }> {
    const response = await salesApi.post(SALES_API.CLUB_ENTRY_ASSIGN_CARDS_FROM_RESERVATION, dto);
    return toCamelCase(response.data);
  },

  async getCardsBySale(saleReceiptId: number): Promise<ClubEntrySaleCardDto[]> {
    const response = await salesApi.get(SALES_API.CLUB_ENTRY_CARDS_BY_SALE(saleReceiptId));
    return toCamelCase(response.data);
  },

  async lookupCard(cardUID: string): Promise<CardLookupResultDto> {
    const response = await salesApi.get(SALES_API.CLUB_ENTRY_CARD_LOOKUP(cardUID));
    return toCamelCase(response.data);
  },

  async getActiveCards(params?: { screenId?: number; page?: number; pageSize?: number; search?: string }): Promise<{ data: ClubEntrySaleCardDto[]; totalCount: number; page: number; pageSize: number; totalPages: number }> {
    const response = await salesApi.get(SALES_API.CLUB_ENTRY_ACTIVE_CARDS(params));
    const raw = toCamelCase(response.data);
    return {
      data: Array.isArray(raw.data) ? raw.data : [],
      totalCount: raw.totalCount || 0,
      page: raw.page || 1,
      pageSize: raw.pageSize || 25,
      totalPages: raw.totalPages || 0,
    };
  },

  async getActiveCardsSummary(screenId?: number): Promise<{ totalActiveCards: number; totalInsideCards: number }> {
    const response = await salesApi.get(SALES_API.CLUB_ENTRY_ACTIVE_CARDS_SUMMARY(screenId));
    const raw = toCamelCase(response.data);
    return {
      totalActiveCards: raw.totalActiveCards || 0,
      totalInsideCards: raw.totalInsideCards || 0,
    };
  },

  /** Gün sonu raporu için kulüp giriş depozito özeti (tarih + opsiyonel şube/ekran filtreli). */
  async getDepositsSummary(params: { startDate: string; endDate: string; branchId?: string; screenId?: number }): Promise<ClubDepositSummaryDto> {
    const response = await salesApi.get(SALES_API.CLUB_ENTRY_DEPOSITS_SUMMARY(params));
    const raw = toCamelCase(response.data);
    return {
      collectedCash: raw.collectedCash || 0,
      collectedPos: raw.collectedPos || 0,
      collectedTotal: raw.collectedTotal || 0,
      collectedCount: raw.collectedCount || 0,
      refundedTotal: raw.refundedTotal || 0,
      refundedCount: raw.refundedCount || 0,
      netTotal: raw.netTotal || 0,
    };
  },

  /** Satış/ödeme olmadan karta geçiş hakkı tanımlar (Kart Yönetimi ekranı). */
  async grantEntryRights(dto: GrantEntryRightsDto): Promise<{ success: boolean; message: string; card: ClubEntrySaleCardDto }> {
    const response = await salesApi.post(SALES_API.CLUB_ENTRY_GRANT_ENTRY_RIGHTS, dto);
    return toCamelCase(response.data);
  },

  /** Verilen kart UID'leri için kalan geçiş haklarını toplu getirir. */
  async getEntryRightsByUids(cardUIDs: string[]): Promise<CardEntryRightsDto[]> {
    const response = await salesApi.post(SALES_API.CLUB_ENTRY_RIGHTS_BY_UIDS, { cardUIDs });
    const raw = toCamelCase(response.data);
    return Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
  },

  /** Sayaç bazlı kartın geçiş hakkını elle düşürür (artıramaz). */
  async consumeEntryRights(cardUID: string, count: number, performedBy?: string): Promise<{ success: boolean; message: string; card: ClubEntrySaleCardDto }> {
    const response = await salesApi.post(SALES_API.CLUB_ENTRY_CONSUME_ENTRY_RIGHTS, { cardUID, count, performedBy });
    return toCamelCase(response.data);
  },

  async checkAndConsume(cardUID: string, doorId: string, doorName?: string): Promise<CheckAndConsumeResultDto> {
    const response = await salesApi.post(SALES_API.CLUB_ENTRY_CHECK_AND_CONSUME, {
      cardUID,
      doorId,
      doorName
    });
    return toCamelCase(response.data);
  },

  async returnCard(cardUID: string, returnedBy?: string): Promise<{ success: boolean; message: string; card: ClubEntrySaleCardDto }> {
    const response = await salesApi.post(SALES_API.CLUB_ENTRY_RETURN_CARD, {
      cardUID,
      returnedBy
    });
    return toCamelCase(response.data);
  },

  async markDepositRefunded(cardUID: string): Promise<{ success: boolean; message: string; card: ClubEntrySaleCardDto }> {
    const response = await salesApi.post(SALES_API.CLUB_ENTRY_MARK_DEPOSIT_REFUNDED, {
      cardUID
    });
    return toCamelCase(response.data);
  },

  /** İçeride ön ödemeli modda kart okutulunca karta tanımlı kalan ürün haklarını getirir. */
  async getCardEntitlements(cardUID: string): Promise<CardEntitlementDto[]> {
    const response = await salesApi.get(SALES_API.CLUB_ENTRY_CARD_ENTITLEMENTS(cardUID));
    const raw = toCamelCase(response.data);
    return Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw) ? raw : []);
  },

  /** 0 TL ürün teslim edilince kullanımı işler + stok çıkışı oluşturur. */
  async consumeEntitlements(cardUID: string, items: ConsumeEntitlementItem[], opts?: { screenId?: number; consumedBy?: string }): Promise<{ success: boolean; consumed: number }> {
    const response = await salesApi.post(SALES_API.CLUB_ENTRY_CONSUME_ENTITLEMENTS, {
      cardUID,
      screenId: opts?.screenId,
      consumedBy: opts?.consumedBy,
      items,
    });
    return toCamelCase(response.data);
  }
};
